import React, { useCallback, useEffect, useState } from 'react';
import { FiRefreshCw, FiAlertTriangle, FiFileText, FiX, FiPlus, FiTrash2, FiCheck, FiRotateCcw, FiSlash, FiCloud, FiClock } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { financeAPI } from '../../services/api';
import * as fs from './financeStyles';

const emptyLine = () => ({ account: '', debit: '', credit: '', memo: '' });
const emptyForm = () => ({ entity: '', period: '', date: '', currency: '', entry_type: 'STANDARD', memo: '', reference: '', lines: [emptyLine(), emptyLine()] });

const JournalEntries = () => {
  const { isFinanceLeadership } = useAuth();
  const [entities, setEntities] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [entries, setEntries] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [entRes, perRes, curRes, accRes, entriesRes] = await Promise.all([
        financeAPI.entities.list(),
        financeAPI.periods.list(),
        financeAPI.currencies.list(),
        financeAPI.accounts.list(),
        financeAPI.entries.list(statusFilter ? { status: statusFilter } : {}),
      ]);
      setEntities(entRes.data.results || entRes.data || []);
      setPeriods(perRes.data.results || perRes.data || []);
      setCurrencies(curRes.data.results || curRes.data || []);
      setAccounts(accRes.data.results || accRes.data || []);
      setEntries(entriesRes.data.results || entriesRes.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Failed to load journal entries.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const updateLine = (idx, field, value) => {
    setForm((prev) => {
      const lines = [...prev.lines];
      lines[idx] = { ...lines[idx], [field]: value };
      return { ...prev, lines };
    });
  };

  const addLine = () => setForm((prev) => ({ ...prev, lines: [...prev.lines, emptyLine()] }));
  const removeLine = (idx) => setForm((prev) => ({ ...prev, lines: prev.lines.filter((_, i) => i !== idx) }));

  const debitTotal = form.lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const creditTotal = form.lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);

  const submitForm = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        lines: form.lines.filter((l) => l.account && (l.debit || l.credit)).map((l) => ({
          account: l.account, debit: l.debit || '0', credit: l.credit || '0', memo: l.memo,
        })),
      };
      await financeAPI.entries.create(payload);
      setForm(emptyForm());
      setShowForm(false);
      load();
    } catch (e2) {
      setError(e2?.response?.data?.error || JSON.stringify(e2?.response?.data) || 'Failed to create draft entry.');
    } finally {
      setSaving(false);
    }
  };

  const doAction = async (entry, action) => {
    if (action === 'reverse' && !window.confirm(`Reverse JE#${entry.id}? This posts a new offsetting entry — the original is never edited.`)) return;
    if (action === 'void' && !window.confirm(`Discard draft JE#${entry.id}?`)) return;
    setBusyId(entry.id);
    setError(null);
    try {
      if (action === 'post') await financeAPI.entries.post(entry.id);
      else if (action === 'reverse') await financeAPI.entries.reverse(entry.id);
      else if (action === 'void') await financeAPI.entries.void(entry.id);
      load();
    } catch (e) {
      setError(e?.response?.data?.error || `Failed to ${action} entry.`);
    } finally {
      setBusyId(null);
    }
  };

  const entityName = (id) => entities.find((e) => e.id === id)?.name || '—';
  const accountLabel = (id) => { const a = accounts.find((x) => x.id === id); return a ? `${a.code} — ${a.name}` : id; };
  const filteredAccounts = accounts.filter((a) => a.is_postable && (!form.entity || String(a.entity) === String(form.entity) || !a.entity));
  const filteredPeriods = periods.filter((p) => !form.entity || String(p.entity) === String(form.entity));

  return (
    <div style={fs.page}>
      <div style={fs.pageHeader}>
        <div>
          <h1 style={fs.pageTitle}><FiFileText size={22} />Journal Entries</h1>
          <p style={fs.pageSubtitle}>Every posted entry is balanced and immutable. Corrections are reversing entries, never edits.</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={fs.select}>
            <option value="">All statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="POSTED">Posted</option>
            <option value="VOID">Void</option>
          </select>
          <button onClick={() => setShowForm((v) => !v)} style={fs.primaryButton}><FiPlus size={14} />New Draft</button>
          <button onClick={load} title="Refresh" style={fs.iconButton}>
            <FiRefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          </button>
        </div>
      </div>

      {error && (
        <div style={fs.errorBanner}>
          <FiAlertTriangle size={18} /><span>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: fs.colors.red, cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      {showForm && (
        <form onSubmit={submitForm} style={{ ...fs.card, marginBottom: 22 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
            <div>
              <div style={fs.label}>Entity</div>
              <select required value={form.entity} onChange={(e) => setForm({ ...form, entity: e.target.value, period: '' })} style={{ ...fs.select, width: '100%' }}>
                <option value="">Select…</option>
                {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div>
              <div style={fs.label}>Period</div>
              <select required value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} style={{ ...fs.select, width: '100%' }}>
                <option value="">Select…</option>
                {filteredPeriods.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.state})</option>)}
              </select>
            </div>
            <div>
              <div style={fs.label}>Date</div>
              <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} style={{ ...fs.input, width: '100%' }} />
            </div>
            <div>
              <div style={fs.label}>Currency</div>
              <select required value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} style={{ ...fs.select, width: '100%' }}>
                <option value="">Select…</option>
                {currencies.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
              </select>
            </div>
            <div>
              <div style={fs.label}>Reference (optional)</div>
              <input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} style={{ ...fs.input, width: '100%' }} />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={fs.label}>Memo</div>
            <input required value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} placeholder="What is this entry for?" style={{ ...fs.input, width: '100%' }} />
          </div>

          <div style={fs.label}>Lines</div>
          {form.lines.map((line, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              <select value={line.account} onChange={(e) => updateLine(idx, 'account', e.target.value)} style={{ ...fs.select, flex: 2 }}>
                <option value="">Account…</option>
                {filteredAccounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
              </select>
              <input type="number" step="0.01" placeholder="Debit" value={line.debit} onChange={(e) => updateLine(idx, 'debit', e.target.value)} style={{ ...fs.input, width: 110 }} />
              <input type="number" step="0.01" placeholder="Credit" value={line.credit} onChange={(e) => updateLine(idx, 'credit', e.target.value)} style={{ ...fs.input, width: 110 }} />
              <input placeholder="Line memo (optional)" value={line.memo} onChange={(e) => updateLine(idx, 'memo', e.target.value)} style={{ ...fs.input, flex: 1 }} />
              <button type="button" onClick={() => removeLine(idx)} style={{ ...fs.iconButton, padding: 8 }}><FiTrash2 size={13} /></button>
            </div>
          ))}
          <button type="button" onClick={addLine} style={{ ...fs.neutralButton, marginBottom: 16 }}><FiPlus size={12} style={{ marginRight: 4, verticalAlign: -1 }} />Add Line</button>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 12, color: debitTotal === creditTotal ? fs.colors.green : fs.colors.amber }}>
              Debit: {debitTotal.toFixed(2)} · Credit: {creditTotal.toFixed(2)}
              {debitTotal !== creditTotal && ' — must balance before posting'}
            </div>
            <button type="submit" disabled={saving} style={fs.primaryButton}>{saving ? 'Saving…' : 'Save Draft'}</button>
          </div>
        </form>
      )}

      {loading ? (
        <div style={fs.loadingState}>Loading…</div>
      ) : entries.length === 0 ? (
        <div style={fs.emptyState}><FiFileText size={44} style={{ marginBottom: 16 }} /><div style={{ fontSize: 15, color: fs.colors.textGhost }}>No journal entries yet.</div></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {entries.map((entry) => {
            const busy = busyId === entry.id;
            const expanded = expandedId === entry.id;
            const color = fs.STATUS_COLORS[entry.status] || '#888';
            return (
              <div key={entry.id} style={fs.card}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, cursor: 'pointer' }} onClick={() => setExpandedId(expanded ? null : entry.id)}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ color: fs.colors.textFaint, fontSize: 12, fontFamily: 'monospace' }}>JE#{entry.id}</span>
                      <span style={{ color: fs.colors.textBright, fontWeight: 600, fontSize: 14 }}>{entry.memo}</span>
                      <span style={fs.badge(color)}>{entry.status}</span>
                      {entry.reference === 'quick-entry' && (
                        entry.synced_to_sheet_at ? (
                          <span style={fs.badge(fs.colors.green)} title={`Synced to spreadsheet ${entry.synced_to_sheet_at}`}>
                            <FiCloud size={11} style={{ marginRight: 3, verticalAlign: -1 }} />Synced
                          </span>
                        ) : (
                          <span style={fs.badge(fs.colors.amber)} title="Not yet synced to the backup spreadsheet">
                            <FiClock size={11} style={{ marginRight: 3, verticalAlign: -1 }} />Sync pending
                          </span>
                        )
                      )}
                    </div>
                    <div style={{ color: fs.colors.textFaint, fontSize: 12, marginTop: 4 }}>
                      {entityName(entry.entity)} · {entry.date} · {entry.currency} {entry.reference ? `· Ref: ${entry.reference}` : ''}
                    </div>
                  </div>
                  {isFinanceLeadership() && (
                    <div style={{ display: 'flex', gap: 8 }} onClick={(e) => e.stopPropagation()}>
                      {entry.status === 'DRAFT' && (
                        <>
                          <button onClick={() => doAction(entry, 'post')} disabled={busy} style={fs.successButton}><FiCheck size={12} style={{ marginRight: 4, verticalAlign: -1 }} />Post</button>
                          <button onClick={() => doAction(entry, 'void')} disabled={busy} style={fs.neutralButton}><FiSlash size={12} style={{ marginRight: 4, verticalAlign: -1 }} />Discard</button>
                        </>
                      )}
                      {entry.status === 'POSTED' && !entry.is_reversed && (
                        <button onClick={() => doAction(entry, 'reverse')} disabled={busy} style={fs.dangerButton}><FiRotateCcw size={12} style={{ marginRight: 4, verticalAlign: -1 }} />Reverse</button>
                      )}
                    </div>
                  )}
                </div>
                {expanded && (
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${fs.colors.cardBorder}` }}>
                    <table style={{ width: '100%', fontSize: 12 }}>
                      <thead>
                        <tr>
                          <th style={{ ...fs.th, padding: '4px 8px' }}>Account</th>
                          <th style={{ ...fs.th, padding: '4px 8px' }}>Debit</th>
                          <th style={{ ...fs.th, padding: '4px 8px' }}>Credit</th>
                          <th style={{ ...fs.th, padding: '4px 8px' }}>Memo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entry.lines.map((l) => (
                          <tr key={l.id}>
                            <td style={{ padding: '4px 8px', color: fs.colors.text }}>{accountLabel(l.account)}</td>
                            <td style={{ padding: '4px 8px', color: fs.colors.text }}>{parseFloat(l.debit) > 0 ? l.debit : ''}</td>
                            <td style={{ padding: '4px 8px', color: fs.colors.text }}>{parseFloat(l.credit) > 0 ? l.credit : ''}</td>
                            <td style={{ padding: '4px 8px', color: fs.colors.textFaint }}>{l.memo}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default JournalEntries;
